import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { HTTP_STATUS } from "@/constants";
import { Button } from "@/components/ui/button";
import StatsCards from "@/components/admin/StatsCards";
import QuotesTable from "@/components/admin/QuotesTable";
import { toast } from "sonner";
import { LogOut, Home, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const fetchData = useCallback(async (setLoadingState) => {
    if (!token) return;
    try {
      const [quotesRes, statsRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: authHeaders }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders })
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        logout();
        navigate('/admin');
        toast.error("Session expired. Please login again.");
      }
    } finally {
      if (setLoadingState) setLoadingState(false);
    }
  }, [token, authHeaders, logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }
    fetchData((val) => { setLoading(val); setRefreshing(val); });
  }, [isAuthenticated, navigate, fetchData]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchData((val) => setRefreshing(val));
  }, [fetchData]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/admin');
    toast.success("Logged out successfully");
  }, [logout, navigate]);

  const handleStatusUpdate = useCallback(async (quoteId, newStatus) => {
    try {
      await axios.patch(
        `${API}/quotes/${quoteId}`,
        { status: newStatus },
        { headers: authHeaders }
      );
      setQuotes(prevQuotes => prevQuotes.map(q => 
        q.id === quoteId ? { ...q, status: newStatus } : q
      ));
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
    }
  }, [authHeaders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin Dashboard | TJP Carpentry & Construction</title>
      </Helmet>

      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-md flex items-center justify-center">
              <span className="text-white font-black text-lg font-['Chivo']">TJP</span>
            </div>
            <div className="hidden sm:block">
              <p className="font-bold text-slate-900 text-sm leading-none">Admin Dashboard</p>
              <p className="text-xs text-stone-500">Quote Management</p>
            </div>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-button">
              <RefreshCw className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} size={16} />
              Refresh
            </Button>
            <Link to="/">
              <Button variant="ghost" size="sm" data-testid="view-site-button">
                <Home className="mr-2" size={16} />
                View Site
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="logout-button">
              <LogOut className="mr-2" size={16} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <StatsCards stats={stats} formatCurrency={formatCurrency} />
        <QuotesTable quotes={quotes} formatCurrency={formatCurrency} onStatusUpdate={handleStatusUpdate} />
      </main>
    </div>
  );
};

export default AdminDashboardPage;
